import React, { useState, useRef } from 'react';
import {
  ChevronRight,
  ChevronDown,
  FileText,
  Users,
  User,
  Plus,
  MoreVertical,
  Copy,
  Trash2,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useSavedReports, useDeleteSavedReport, useDuplicateSavedReport } from '@/hooks/useSavedReports';
import type { SavedReportWithMeta } from '@/types/savedReports';

interface SavedReportsSidebarProps {
  currentReportId: string | null;
  onSelectReport: (report: SavedReportWithMeta) => void;
  onNewReport: () => void;
  onSaveReport: () => void;
  hasUnsavedChanges: boolean;
}

export const SavedReportsSidebar: React.FC<SavedReportsSidebarProps> = ({
  currentReportId,
  onSelectReport,
  onNewReport,
  onSaveReport,
  hasUnsavedChanges,
}) => {
  const { data: reports, isLoading, error } = useSavedReports();
  const deleteMutation = useDeleteSavedReport();
  const duplicateMutation = useDuplicateSavedReport();

  const [myReportsExpanded, setMyReportsExpanded] = useState(true);
  const [teamReportsExpanded, setTeamReportsExpanded] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; report: SavedReportWithMeta | null }>({
    show: false,
    report: null,
  });

  const myReports = reports?.filter(r => r.isOwner && r.visibility === 'personal') || [];
  const teamReports = reports?.filter(r => r.visibility === 'team') || [];

  const handleDelete = (report: SavedReportWithMeta) => {
    setDeleteConfirm({ show: true, report });
  };

  const confirmDelete = () => {
    if (deleteConfirm.report) {
      deleteMutation.mutate(deleteConfirm.report.id);
    }
    setDeleteConfirm({ show: false, report: null });
  };

  const handleDuplicate = (report: SavedReportWithMeta) => {
    duplicateMutation.mutate({
      id: report.id,
      newName: `${report.name} (Copy)`,
    });
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
    });
  };

  const ReportItem: React.FC<{ report: SavedReportWithMeta }> = ({ report }) => {
    const isActive = report.id === currentReportId;

    return (
      <div
        className={`group flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer ${
          isActive ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-100'
        }`}
        onClick={() => onSelectReport(report)}
      >
        <FileText className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
        <div className="flex-1 min-w-0">
          <div className={`text-sm truncate ${isActive ? 'font-medium' : ''}`}>
            {report.name}
          </div>
          <div className="text-xs text-gray-400 truncate">
            {formatDate(report.updated_at)}
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <button className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-gray-200">
              <MoreVertical className="h-4 w-4 text-gray-500" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleDuplicate(report)}>
              <Copy className="h-4 w-4 mr-2" />
              Duplicate
            </DropdownMenuItem>
            {report.isOwner && (
              <DropdownMenuItem
                onClick={() => handleDelete(report)}
                className="text-red-600 focus:text-red-600"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  };

  const SectionHeader: React.FC<{
    title: string;
    icon: React.ReactNode;
    expanded: boolean;
    onToggle: () => void;
    count: number;
  }> = ({ title, icon, expanded, onToggle, count }) => (
    <button
      onClick={onToggle}
      className="w-full flex items-center gap-2 px-2 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-md"
    >
      {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      {icon}
      <span className="flex-1 text-left">{title}</span>
      <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
        {count}
      </span>
    </button>
  );

  return (
    <div className="h-full flex flex-col bg-white border-r border-gray-200">
      {/* Header */}
      <div className="p-3 border-b border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold text-gray-900">Saved Reports</h2>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={onNewReport}
            variant="outline"
            size="sm"
            className="flex-1"
          >
            <Plus className="h-4 w-4 mr-1" />
            New
          </Button>
          <Button
            onClick={onSaveReport}
            size="sm"
            className="flex-1"
            disabled={!hasUnsavedChanges}
          >
            Save
          </Button>
        </div>
      </div>

      {/* Reports List */}
      <div className="flex-1 overflow-y-auto p-2">
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          </div>
        )}

        {error && (
          <div className="text-sm text-red-500 p-2">
            Failed to load reports
          </div>
        )}

        {!isLoading && !error && (
          <>
            {/* Team Reports - shown first */}
            <div className="mb-3">
              <SectionHeader
                title="Team Reports"
                icon={<Users className="h-4 w-4" />}
                expanded={teamReportsExpanded}
                onToggle={() => setTeamReportsExpanded(!teamReportsExpanded)}
                count={teamReports.length}
              />
              {teamReportsExpanded && (
                <div className="mt-1 space-y-0.5">
                  {teamReports.length === 0 ? (
                    <div className="text-xs text-gray-400 px-2 py-2">
                      No team reports yet
                    </div>
                  ) : (
                    teamReports.map(report => (
                      <ReportItem key={report.id} report={report} />
                    ))
                  )}
                </div>
              )}
            </div>

            {/* My Reports */}
            <div>
              <SectionHeader
                title="My Reports"
                icon={<User className="h-4 w-4" />}
                expanded={myReportsExpanded}
                onToggle={() => setMyReportsExpanded(!myReportsExpanded)}
                count={myReports.length}
              />
              {myReportsExpanded && (
                <div className="mt-1 space-y-0.5">
                  {myReports.length === 0 ? (
                    <div className="text-xs text-gray-400 px-2 py-2">
                      No personal reports yet
                    </div>
                  ) : (
                    myReports.map(report => (
                      <ReportItem key={report.id} report={report} />
                    ))
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirm.show} onOpenChange={(open) => !open && setDeleteConfirm({ show: false, report: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Report</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteConfirm.report?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
